import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { EventServicesService } from 'src/app/services/event-services.service';
import { MessageService } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { JobServicesService } from 'src/app/services/job-services.service';
import { EmailServicesService } from 'src/app/services/email-services.service';
import { SharedServicesService } from 'src/app/services/shared-services.service';
import { FormBuilderService } from 'src/app/services/form-builder.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-events-add',
    templateUrl: './events-add.component.html',
    styleUrls: ['./events-add.component.css'],
    providers: [MessageService, DatePipe],
    standalone: false
})
export class EventsAddComponent implements OnInit, OnChanges {

  @Input() eventData: any;
  @Input() eventJobData: any;
  @Input() eventCloneData: any;
  @Input() isEventCloneData: boolean = false;
  @Input() inEditMode: boolean = false;

  @Output() closeEventSlider = new EventEmitter<boolean>();

  institutions:  any[] = [];
  cloneEvent: boolean = false;
  clicked: boolean = false;
  saveBtnLoading: boolean = false;
  publishBtnLoading: boolean = false;
  gettingRoles: boolean = false;

  eventTypes: Array<any> = [];
  eventCategories: Array<any> = [];
  jobRoles: Array<any> = [];
  selectedJobRoles: any[] = [];
  selectedInstitutions: any[] = [];
  owners: Array<any> = [];
  ownersDetails: Array<any> = [];
  regForms: Array<any> = [];
  emailTemplates: Array<any> = [];

  minSalary!: number;
  maxSalary!: number;

  //Dates
  eventStartDate: any;
  eventEndDate: any
  registrationStartDate: any;
  registrationEndDate: any;

  currentDate = new Date();
  renderForm = false;

  addEventForm: FormGroup = new FormGroup({
    eventTitle: new FormControl('', Validators.required),
    eventStartDate: new FormControl('', Validators.required),
    eventEndDate: new FormControl('', Validators.required),
    jobRole: new FormControl({ value: [], disabled: false }, Validators.required),
    workExperience: new FormControl(null, [Validators.min(0), Validators.max(50)]),
    minSalary: new FormControl(null, [Validators.min(0)]),
    maxSalary: new FormControl(null, [Validators.min(0), (control: AbstractControl) => Validators.min(this.minSalary)(control)]),
    eventType: new FormControl(null, Validators.required),
    eventCategory: new FormControl({ value: null, disabled: false }, Validators.required),
    institutionName: new FormControl(null),
    institutionAddress: new FormControl(null),
    registrationStartDate: new FormControl({ value: '', disabled: true }, Validators.required),
    registrationEndDate: new FormControl({ value: '', disabled: true }, Validators.required),
    reg_form_uuid: new FormControl(null, Validators.required),
    email_template_id: new FormControl(null, Validators.required),
    // <!-- For now owner details are commented out from event -->
    // createdRole: new FormControl(null, Validators.required),
    // createdBy: new FormControl(null, Validators.required),
    status: new FormControl(''),
    enableProctoring: new FormControl(false),
    photoCaptureMode: new FormControl('live'),
  })

  constructor(
    private eventServices: EventServicesService,
    private messageService: MessageService,
    private jobServices: JobServicesService,
    private emailServices: EmailServicesService,
    private sharedService: SharedServicesService,
    private formBuilderService: FormBuilderService
  ) { }

  private async ensureProctoringPhotoField(): Promise<boolean> {
    const warning = await firstValueFrom(this.formBuilderService.proctoringPhotoFieldWarning(
      this.addEventForm.controls['enableProctoring'].value,
      this.addEventForm.controls['reg_form_uuid'].value
    ));
    if (warning) {
      this.messageService.add({ severity: 'warn', summary: 'Photo field required', detail: warning });
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.getFormData();

    this.addEventForm.get('eventStartDate')?.valueChanges.subscribe(() => {
      this.updateRegistrationDateState();
    });
    
    this.addEventForm.get('eventEndDate')?.valueChanges.subscribe(() => {
      this.updateRegistrationDateState();
    });

    this.addEventForm.get('jobRole')?.valueChanges.subscribe((selectedRoles: any[]) => {
      this.selectedJobRoles = selectedRoles || [];
    });

    this.addEventForm.get('institutionName')?.valueChanges.subscribe((selectedInstitutions: any[]) => {
      this.selectedInstitutions = selectedInstitutions || [];
    });

    // Commented out owner details logic
    // this.addEventForm.get('createdRole')?.valueChanges.subscribe((roleId: number) => {
    //   if (roleId) {
    //     this.getOwnerDetails(roleId);
    //   } else {
    //     this.ownersDetails = [];
    //   }
    // });
  }

  updateRegistrationDateState() {
    const startDate = this.addEventForm.get('eventStartDate')?.value;
    const endDate = this.addEventForm.get('eventEndDate')?.value;

    if (startDate && endDate) {
      this.addEventForm.get('registrationStartDate')?.enable();
      this.addEventForm.get('registrationEndDate')?.enable();
    } else {
      this.addEventForm.get('registrationStartDate')?.disable();
      this.addEventForm.get('registrationEndDate')?.disable();
    }
  }

  getFormData = async () => {
    this.gettingRoles = true;
    try {
      const response = await this.eventServices.getEventFormDataFastApi().toPromise();

      if (response && response.status === 200 && response.body) {
        this.jobRoles = response.body.job_roles;
        this.owners = response.body.roles;
        this.emailTemplates = response.body.email_templates;
        this.eventCategories = response.body.event_categories;
        this.eventTypes = response.body.event_types;

        this.institutions = response.body.institutes;
        this.regForms = response.body.forms;
        this.gettingRoles = false;
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
    } finally {
      this.gettingRoles = false;
    }
  };

  onEventTypeChange() {
    const selectedEventType = this.getSelectedEventType();
    const institutionNameControl = this.addEventForm.get('institutionName');
    const institutionAddressControl = this.addEventForm.get('institutionAddress');
    const currentValue = institutionNameControl?.value;

    if (!institutionNameControl || !institutionAddressControl) return;

    if (selectedEventType?.institution_select_type === 'not_applicable') {
      institutionNameControl.clearValidators();
      institutionAddressControl.clearValidators();
      institutionNameControl.setValue([]);
    } else if (selectedEventType?.institution_select_type === 'single') {
      institutionNameControl.setValidators([Validators.required]);
      institutionAddressControl.setValidators([Validators.required]);
      
      institutionNameControl.setValue(Array.isArray(currentValue) ? currentValue[0] || null : currentValue);
    } else {
      // Multiple select case
      institutionNameControl.setValidators([Validators.required]);
      institutionAddressControl.setValidators([Validators.required]);
      // Convert single value to array or keep array
      institutionNameControl.setValue(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []);
    }

    institutionNameControl.updateValueAndValidity();
    institutionAddressControl.updateValueAndValidity();
  }

  getSelectedEventType() {
    const selectedId = this.addEventForm.value.eventType;
    return this.eventTypes.find(eventType => eventType.id === selectedId);
  }
  async ngOnChanges(changes: SimpleChanges) {
    if (changes['eventCloneData']) {
      this.setValues(this.eventCloneData);
    }

    if (changes['eventData']) {
      // this.inEditMode = Object.keys(this.eventData).length ? true : false;
      if (this.inEditMode) this.setValues(this.eventData);
    }
  }

  async setValues(eventData: any) {
    if (this.inEditMode || this.isEventCloneData) {
      this.renderForm = true;
      try {
        const response = await this.eventServices.getSpecificEventFastApi(eventData.id).toPromise();
  
        if (response?.status === 200 && response.body) {
          const { event_details } = response.body;          
          this.addEventForm.get('eventType')?.setValue(event_details.event_type.id);          
          await new Promise(resolve => setTimeout(resolve, 100));
          const selectedEventType = this.getSelectedEventType();          
          this.addEventForm.get('institutionName')?.setValue(null);
          this.addEventForm.get('institutionAddress')?.setValue(null);          
          if (event_details.institutes?.length > 0) {            
            const institutionValue = selectedEventType?.institution_select_type === 'single' 
              ? event_details.institutes[0].id
              : event_details.institutes.map((inst: any) => inst.id);            
            
            this.addEventForm.get('institutionName')?.setValue(institutionValue);
          }
          if (event_details.institution_address) {
            this.addEventForm.get('institutionAddress')?.setValue(event_details.institution_address);
          }          
          const formValues = {
            eventTitle: event_details.name,
            jobRole: event_details.job_roles.map((role: any) => role.id),
            workExperience: event_details.work_experience,
            minSalary: event_details.min_salary,
            maxSalary: event_details.max_salary,
            eventCategory: event_details.event_category.id,
            reg_form_uuid: event_details.form.uuid,
            email_template_id: event_details.email_template.id,
            enableProctoring: event_details.proctoring_config?.enabled ?? false,
            photoCaptureMode: event_details.proctoring_config?.mode ?? 'live'
          };
          this.addEventForm.patchValue(formValues);
          if (this.inEditMode) {
            const dates = {
              eventStartDate: new Date(event_details.event_start_date),
              eventEndDate: new Date(event_details.event_end_date),
              registrationStartDate: new Date(event_details.registration_start_date),
              registrationEndDate: new Date(event_details.registration_end_date)
            };
            this.addEventForm.patchValue(dates);
          }
          const institutionNameControl = this.addEventForm.get('institutionName');
          const institutionAddressControl = this.addEventForm.get('institutionAddress');
  
          institutionNameControl?.clearValidators();
          institutionAddressControl?.clearValidators();
  
          if (selectedEventType?.institution_select_type === 'single' || 
              selectedEventType?.institution_select_type === 'multiple') {
            institutionNameControl?.setValidators([Validators.required]);
            institutionAddressControl?.setValidators([Validators.required]);
          }
  
          institutionNameControl?.updateValueAndValidity();
          institutionAddressControl?.updateValueAndValidity();
        }
      } catch (error) {
        console.error('Error in setValues:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to fetch event details: ${error}`
        });
      }
    }
  }
  updateEvent = async (payload: FormGroup) => {
    this.publishBtnLoading = true;
    this.clicked = true;
    const fieldsToCheck = ['eventTitle', 'institutionAddress'];
    if (this.sharedService.hasSecurityViolations(this.addEventForm, fieldsToCheck)) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Invalid Input', 
        detail: 'Invalid input format in some fields.' 
      });
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    if (!this.addEventForm.valid) {
      this.publishBtnLoading = false;
      return;
    }
    
    const isTemplateValid = await this.validateEmailTemplate();
    if (!isTemplateValid) {
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    const photoFieldValid = await this.ensureProctoringPhotoField();
    if (!photoFieldValid) {
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    const secureFormValue = this.sharedService.getSecurePayload(this.addEventForm.value);
    const updatePayload = this.createPayload(secureFormValue);
    
    this.eventServices.updateEventFastApi(updatePayload, this.eventData.id).subscribe({
      next: (response) => {
        if (response.status === 200) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: response.body.detail });
          setTimeout(() => {
            this.closeEventSlider.emit(true);
          }, 1000);
        }
        this.publishBtnLoading = false;
        this.clicked = false;
      },
      error: (err) => {
        this.publishBtnLoading = false;
        this.clicked = false;
      }
    });
  }

  publishEvent = async (payload: any) => {
    this.publishBtnLoading = true;
    this.clicked = true;
    const fieldsToCheck = ['eventTitle', 'institutionAddress'];
    if (this.sharedService.hasSecurityViolations(this.addEventForm, fieldsToCheck)) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Invalid Input', 
        detail: 'Invalid input format in some fields.' 
      });
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    if (!this.addEventForm.valid) {
      this.publishBtnLoading = false;
      return;
    }

    const isTemplateValid = await this.validateEmailTemplate();
    if (!isTemplateValid) {
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    const photoFieldValid = await this.ensureProctoringPhotoField();
    if (!photoFieldValid) {
      this.clicked = false;
      this.publishBtnLoading = false;
      return;
    }

    const secureFormValue = this.sharedService.getSecurePayload(this.addEventForm.value);
    const publishPayload = this.createPayload(secureFormValue);
    
    this.eventServices.createEventFastApi(publishPayload).subscribe({
      next: (response) => {
        if (response.status === 201) {
          this.addEventForm.reset();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: response.body.detail });
          this.publishBtnLoading = false;
          setTimeout(() => {
            this.isEventCloneData = false;
            this.eventCloneData = {};
            this.eventJobData = {};
            this.closeEventSlider.emit(true);
          }, 1500);
        }
        this.clicked = false;
      },
      error: () => {
        this.publishBtnLoading = false;
        this.clicked = false;
      }
    });
  } 

  private createPayload(secureFormValue?: any) {
    const formValue = secureFormValue || this.addEventForm.value;
    const eventType = this.getSelectedEventType();
    const institutionValue = this.addEventForm.controls['institutionName'].value;
    const institutionAddress = formValue.institutionAddress;
    
    let institutes = [];
    if (institutionValue) {
      institutes = Array.isArray(institutionValue) ? institutionValue : [institutionValue];
    }
    
    const jobRoles = this.selectedJobRoles.length > 0 
      ? this.selectedJobRoles 
      : (this.addEventForm.controls['jobRole'].value || []);
    
    return {
      name: formValue.eventTitle,
      event_start_date: this.addEventForm.controls['eventStartDate'].value,
      event_end_date: this.addEventForm.controls['eventEndDate'].value,
      job_roles: jobRoles,
      work_experience: this.addEventForm.controls['workExperience'].value,
      min_salary: this.addEventForm.controls['minSalary'].value,
      max_salary: this.addEventForm.controls['maxSalary'].value,
      event_category_id: this.addEventForm.controls['eventCategory'].value,
      event_type_id: this.addEventForm.controls['eventType'].value,
      registration_start_date: this.addEventForm.controls['registrationStartDate'].value,
      registration_end_date: this.addEventForm.controls['registrationEndDate'].value,
      // For now owner details are commented out from event
      // owner_id: this.addEventForm.controls['createdBy'].value,
      form_uuid: this.addEventForm.controls['reg_form_uuid'].value,
      email_template_id: this.addEventForm.controls['email_template_id'].value,
      institution_address: eventType?.institution_select_type === 'not_applicable' 
        ? (institutionAddress || null)
        : institutionAddress,
      institutes: eventType?.institution_select_type === 'not_applicable'
        ? (institutes.length ? institutes : [])
        : institutes,
      proctoring_config: this.buildPhotoCaptureConfig(),
    };
  }

  private buildPhotoCaptureConfig() {
    const enabled = this.addEventForm.controls['enableProctoring'].value ?? false;
    return {
      enabled,
      mode: enabled ? (this.addEventForm.controls['photoCaptureMode'].value ?? 'live') : null,
    };
  }

  onPublish() {
  }

  onCancel() {
    this.closeEventSlider.emit(true);
  }

resetRegistrationDates() {
  if (!this.renderForm) {
    const institutionName = this.addEventForm.get('institutionName')?.value;
    const institutionAddress = this.addEventForm.get('institutionAddress')?.value;    
    const registrationStartDate = this.addEventForm.get('registrationStartDate');
    const registrationEndDate = this.addEventForm.get('registrationEndDate');
    
    if (registrationStartDate && registrationEndDate) {
      registrationStartDate.setValue(null);
      registrationEndDate.setValue(null);
    }    
    this.addEventForm.patchValue({
      institutionName: institutionName,
      institutionAddress: institutionAddress
    });
  } else {
    this.renderForm = this.inEditMode;
  }
  this.updateRegistrationDateState();
}
decodeHTML(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent || el.innerText || '';
}
private async validateEmailTemplate(): Promise<boolean> {
  const templateId = this.addEventForm.get('email_template_id')?.value;

  if (!templateId) return true;

  try {
    const response = await this.emailServices.getSpecificTemplateFastApi(templateId).toPromise();

    if (response && response.status === 200 && response.body) {
      const rawBody = response.body.body || '';
      const decodedBody = this.decodeHTML(rawBody);
      const hasLinkPlaceholder = decodedBody.includes('${link}') || decodedBody.includes('{link}');      
      
      if (!hasLinkPlaceholder) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid Template',
          detail: 'The selected email template does not contain the required ${link} placeholder. Please choose a valid template.'
        });
        return false;
      }
    }
  } catch (error) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Unable to validate the selected email template.'
    });
    return false;
  }

  return true;
}

  getPostedAgo(dateString: string): string {
    const now = new Date();
    const posted = new Date(dateString);
    const diff = now.getTime() - posted.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days < 1) return 'Today';
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  // For now owner details are commented out from event
  // getOwnerDetails = (roleId: any) => {
  //   this.jobServices.getUserByRoleIdFastApi(roleId).subscribe({
  //     next: (response: any) => {
  //       const ownerDetails = Object.keys(response.body.users).length !== 0 ? response.body.users : [];
  //       this.ownersDetails = ownerDetails.map((owner: any) => ({
  //         name: `${owner.first_name} ${owner.last_name}`,
  //         id: owner.id
  //       }));;
  //     },
  //   });
  // }
}
